//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { useConnectedInstagramAccountQuery } from '@account-settings/accountSettingsQueries';
import {
  RIGHTS_STATUS,
  RightsRequestCommunicationType,
} from '@apis/rights-management/rightsManagementApiModels';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { UnexpectedErrorAlert } from '@uia/UnexpectedErrorAlert';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { UIARightsRequestBody } from '@uia/rights/UIARightsRequestBody';
import {
  FB_IG_NETWORKS,
  RIGHT_STATUSES_REQUESTABLE,
  RIGHT_STATUSES_UNFINISHED,
} from '@uia/rights/constants/uiaRightsConstants';
import { UIARightsRequestContextProvider } from '@uia/rights/context/UIARightsRequestContext';
import { RequestTextSource } from '@uia/rights/forms/RequestText';
import { useGenerativeAiRightsRequestQuery } from '@uia/rights/hooks/useGenerativeAiRightsRequestQuery';
import { useGraphRightsCapabilityQuery } from '@uia/rights/hooks/useGraphRightsCapabilityQuery';
import { useHasRightsFormErrorsQuery } from '@uia/rights/hooks/useHasRightsFormErrorsQuery';
import { useRequestRightsMutation } from '@uia/rights/hooks/useRequestRightsMutation';
import { useShouldShowCopyRightsFlowQuery } from '@uia/rights/hooks/useShouldShowCopyRightsFlowQuery';
import {
  getRequestMetadata,
  getRightsRequestDetailsWithSyndicationCheck,
} from '@uia/rights/utils/rightsRequestUtils';
import { isDefined, isUndefined } from '@utils/filterPredicates';

interface Props {
  cuid: Cuid;
  onCloseModal: () => void;
  onPrevious?: () => void;
}

export function UIARequestRights({ cuid, onCloseModal, onPrevious }: Props) {
  const [rightsRequestText, setRightsRequestText] = useState<string>();
  const [approvalPhrase, setApprovalPhrase] = useState<string | undefined>();
  const [selectedRightsOption, setSelectedRightsOption] =
    useState<IRightsOption>();
  const [requestTextIndex, setRequestTextIndex] = useState(0);

  const [requestType, setRequestType] = useState<RightsRequestOptions>(
    RightsRequestOptions.COMMENTS,
  );

  const [requestTextSource, setRequestTextSource] = useState<RequestTextSource>(
    RequestTextSource.CONFIG,
  );

  const partialCuid = Cuid.toPartial(cuid);

  const {
    data: metadata,
    isError: isErrorMetadata,
    isFetching: isFetchingMetadata,
    isPending: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: isCapable,
    isError: isErrorCapable,
    isPending: isLoadingCapable,
  } = useGraphRightsCapabilityQuery(partialCuid);

  const {
    data: shouldShowCopyRightsFlow,
    isError: isErrorCopyRightsShow,
    isPending: isLoadingCopyRightsShow,
  } = useShouldShowCopyRightsFlowQuery(
    partialCuid,
    metadata?.rightsRequestStatus.status,
  );

  const {
    data: igBusinessId,
    isError: isErrorBusinessId,
    isPending: isLoadingBusinessId,
  } = useConnectedInstagramAccountQuery(
    (data) => data.data.connectedAccount?.businessAccountId,
  );

  const {
    data: hasFormErrors,
    isError: isErrorFormErrors,
    isPending: isLoadingFormErrors,
  } = useHasRightsFormErrorsQuery(rightsRequestText);

  const {
    isError: isErrorRequestRights,
    isPending: isRequestingRights,
    mutate: requestRights,
  } = useRequestRightsMutation(cuid);

  const {
    data: aiGeneratedMessage,
    isError: isErrorGenerativeAi,
    isFetching: isFetchingGenerativeAi,
  } = useGenerativeAiRightsRequestQuery({
    approvalPhrase,
    cuid,
    rightsRequestText,
  });

  const { trackEvent } = useMediaTrackingContext();

  const isLoading =
    isLoadingCapable ||
    isLoadingCopyRightsShow ||
    isLoadingFormErrors ||
    isLoadingBusinessId ||
    isLoadingMetadata;

  if (isLoading) {
    return <StyledSpinner />;
  }

  const hasRequestError =
    isErrorCapable ||
    isErrorCopyRightsShow ||
    isErrorFormErrors ||
    isErrorBusinessId ||
    isErrorRequestRights ||
    isErrorGenerativeAi ||
    isErrorMetadata ||
    isUndefined(metadata);

  if (hasRequestError) {
    return (
      <OverlayWrapper>
        <UnexpectedErrorAlert />
      </OverlayWrapper>
    );
  }

  const { socialNetwork } = metadata;

  const {
    approvalPhrase: defaultApprovalPhrase,
    communicationType,
    requestMessage,
    status,
  } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  if (isUndefined(rightsRequestText) && isDefined(requestMessage)) {
    setRightsRequestText(requestMessage);
  }

  if (status !== RIGHTS_STATUS.CANCELLED) {
    if (
      requestType === RightsRequestOptions.DM &&
      communicationType === RightsRequestCommunicationType.COMMENT
    ) {
      setRequestType(RightsRequestOptions.COMMENTS);
    }

    if (
      requestType === RightsRequestOptions.COMMENTS &&
      communicationType === RightsRequestCommunicationType.DM
    ) {
      setRequestType(RightsRequestOptions.DM);
    }
  }

  if (isUndefined(approvalPhrase) && isDefined(defaultApprovalPhrase)) {
    setApprovalPhrase(defaultApprovalPhrase);
  }

  const isFacebookOrInstagram =
    isDefined(socialNetwork) && FB_IG_NETWORKS.includes(socialNetwork);

  const onRequestRights = () => {
    if (approvalPhrase && rightsRequestText) {
      trackEvent({
        cuid,
        event: MediaTrackingEventName.RightsRequested,
      });
      requestRights({
        approvalPhrase,
        requestForRightsMessage: rightsRequestText,
        shouldUseGraphApi: !!isCapable && isFacebookOrInstagram,
        metadata: getRequestMetadata(
          rightsRequestText,
          requestTextIndex,
          requestTextSource,
          aiGeneratedMessage,
          selectedRightsOption,
        ),
      });
    }
  };

  const isInstaDMSelected = requestType === RightsRequestOptions.DM;

  // Disable request button when
  const isRequestButtonDisabled =
    // - already requesting rights
    isRequestingRights ||
    // - rights request status is not expired, failed, none, or unsent
    !RIGHT_STATUSES_REQUESTABLE.includes(status) ||
    // - there are form errors
    !!hasFormErrors ||
    // - it is an Instagram/facebook post and is graph capable but we don't have a connected account
    (isFacebookOrInstagram && !!isCapable && !igBusinessId) ||
    // Generative AI is loading a new request message
    isFetchingGenerativeAi ||
    // metadata is being fetched
    isFetchingMetadata;

  return (
    <UIARightsRequestContextProvider
      value={{
        requestTextSource,
        setRequestTextSource,
        selectedRightsOption,
        setSelectedRightsOption,
        requestTextIndex,
        setRequestTextIndex,
      }}
    >
      <OverlayWrapper>
        <UIARightsRequestBody
          approvalPhrase={approvalPhrase}
          cuid={cuid}
          onCloseModal={onCloseModal}
          requestType={requestType}
          rightsRequestText={rightsRequestText}
          setApprovalPhrase={setApprovalPhrase}
          setRequestType={setRequestType}
          setRightsRequestText={setRightsRequestText}
        />
        <UIAFooter>
          {!shouldShowCopyRightsFlow &&
          !RIGHT_STATUSES_UNFINISHED.includes(status) &&
          !isInstaDMSelected ? (
            <UIAFooter.Actions>
              {isDefined(onPrevious) && (
                <UIAFooter.ActionButton
                  onClick={onPrevious}
                  trackingId={UIATrackingIds.RIGHTS_REQUEST_BACK}
                >
                  Previous
                </UIAFooter.ActionButton>
              )}
              <UIAFooter.RequiredPermissionsActionButton
                isDisabled={isRequestButtonDisabled}
                onClick={onRequestRights}
                permissionNames={[UIA_RIGHTS_REQUEST_PERMISSION]}
                trackingId={UIATrackingIds.RIGHTS_REQUEST_REQUEST}
              >
                Request
              </UIAFooter.RequiredPermissionsActionButton>
            </UIAFooter.Actions>
          ) : (
            <UIAFooter.Actions>
              {isDefined(onPrevious) && (
                <UIAFooter.ActionButton
                  onClick={onPrevious}
                  trackingId={UIATrackingIds.RIGHTS_REQUEST_BACK}
                >
                  Previous
                </UIAFooter.ActionButton>
              )}
              <UIAFooter.ActionButton
                onClick={onCloseModal}
                trackingId={UIATrackingIds.RIGHTS_REQUEST_DONE}
              >
                Done
              </UIAFooter.ActionButton>
            </UIAFooter.Actions>
          )}
        </UIAFooter>
      </OverlayWrapper>
    </UIARightsRequestContextProvider>
  );
}

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const StyledSpinner = styled(CenteredSpinner)(
  ({ theme }) => `
  margin-top: ${theme.spacings[7]};
`,
);
